/**
 * Platform Admin · Email page, Logs tab
 * HTMX-driven filtering & pagination for
 * ``platform/components/_email_logs_content.html`` (the ?tab=logs tab of
 * ``platform/email_settings.html``).
 *
 * See ``.cursor/rules/hx-boost-init.mdc`` for the canonical pattern.
 * In short: ``autoInitEmailLogs`` can fire several times against the same
 * ``#toggle-filters-btn`` over the lifetime of a session (initial load +
 * every boosted nav landing on the Logs tab + every Back/Forward
 * restoring it from htmx history). Without a per-DOM-node guard, each pass
 * stacks one extra ``click`` listener on the same button → toggling the
 * panel twice (or more) per click → user sees "nothing happens".
 */

export function initEmailLogs(options = {}) {
  const { hasActiveFilters = false } = options;
  initFilterPanelUI(hasActiveFilters);
}

/**
 * Auto-init entry point (wired via ``data-auto-init="emailLogs"``).
 * Called by ``_runAutoInit`` in app.js on initial DOMContentLoaded, on
 * htmx:afterSwap of #platform-content (boosted navigation landing on
 * this page), and on htmx:historyRestore (browser Back/Forward). The
 * page itself swaps its inner ``#list-results`` partial via HTMX, but
 * the root data-auto-init lives on the outer card so its
 * ``data-auto-init-done`` flag survives those inner swaps and the toggle
 * button keeps its single click listener.
 */
export function autoInitEmailLogs() {
  const root = document.querySelector('[data-auto-init="emailLogs"]');
  if (!root) return;
  initEmailLogs({
    hasActiveFilters: root.dataset.hasActiveFilters === '1',
  });
}

/**
 * Filter panel toggle / reset (HTMX handles the data round-trips).
 *
 * Per-element flags (``_emailLogsFilterBound`` / ``_emailLogsResetBound``)
 * prevent double-binding when ``autoInitEmailLogs`` fires more than once
 * against the same DOM node — see file header and hx-boost-init.mdc rule
 * 3. Use a plain JS property, NOT ``dataset.*``, because ``dataset.*``
 * survives outerHTML history snapshots and would falsely report "already
 * bound" on a fresh DOM node restored from cache.
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = panel ? panel.querySelector('form') : null;

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  if (toggleBtn && panel && !toggleBtn._emailLogsFilterBound) {
    toggleBtn._emailLogsFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._emailLogsResetBound) {
    resetBtn._emailLogsResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('select').forEach((sel) => {
        sel.value = '';
      });
      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}
